import { Formik } from 'formik';
import { useCallback } from 'react';
import { object } from 'yup';

import type {
  DimensionType,
  ToleranceType,
  Dimension,
  DimensionRole,
  Orientation,
} from '@frontend/api';
import {
  TOLERANCE_TYPES,
  DIMENSION_ROLES,
  DIMENSION_TYPES,
  ENCLOSURE_SHAPES,
  UNITS,
  enclosureShapeFromDimensionRole,
} from '@frontend/api';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  GDTSymbolText,
  Checkbox,
} from '@frontend/legacy-components';

import { SubFormProps } from '../types';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { MultiplierInput } from './shared/MultiplierInput';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import { ToleranceInputGroup } from './shared/ToleranceInputGroup';
import {
  DimensionTypeSelect,
  DimensionRoleSelect,
  OrientationSelect,
} from './shared/TypedSelects';
import {
  createDimensionTypeValidation,
  createRequiredStringNumberField,
  createStringToleranceValidationGroup,
  createMultiplierValidationGroup,
  parsePrecisionValue,
} from './shared/validation';
import {
  buildPrecisionMeasurementObject,
  stringToleranceFormValuesToConfig,
  extractMultiplier,
} from './shared/valueBuilders';

type DimensionFormValues = {
  dimType: DimensionType;
  value: string;
  hasMult: boolean;
  mult?: number;
  hasTolerance: boolean;
  toleranceType: ToleranceType;
  toleranceUpper?: string;
  toleranceLower?: string;
  toleranceSymmetric?: string;
  isOrigin: boolean;
  isTyp: boolean;
  isStk: boolean;
  isThk: boolean;
  role: DimensionRole;
  orientation?: Orientation;
};

/**
 * Converts DimensionFormValues into a concise string summary using appropriate symbols
 * @param values - The dimension form values to convert
 * @returns A formatted string representation of the dimension
 */
const formatDimensionSummary = (values: DimensionFormValues): string => {
  const isAngular = values.dimType === DIMENSION_TYPES.ANGULAR;
  const unit = isAngular ? '°' : '';

  let prefix = '';
  if (values.hasMult && values.mult !== undefined) {
    prefix += `${values.mult}x `;
  }
  switch (values.dimType) {
    case DIMENSION_TYPES.DIAMETER:
      prefix += '⌀';
      break;
    case DIMENSION_TYPES.RADIUS:
      prefix += 'R';
      break;
  }

  const parts = [`${prefix}${values.value || '0'}${unit}`];

  if (values.hasTolerance) {
    switch (values.toleranceType) {
      case TOLERANCE_TYPES.SYMMETRIC:
        if (values.toleranceSymmetric) {
          parts.push(`±${values.toleranceSymmetric}${unit}`);
        }
        break;
      case TOLERANCE_TYPES.ASYMMETRIC:
        if (values.toleranceUpper && values.toleranceLower) {
          parts.push(
            `+${values.toleranceUpper}${unit}/-${values.toleranceLower}${unit}`,
          );
        }
        break;
      case TOLERANCE_TYPES.LIMIT:
        if (values.toleranceUpper && values.toleranceLower) {
          parts.push(
            `[${values.toleranceLower}${unit}, ${values.toleranceUpper}${unit}]`,
          );
        }
        break;
    }
  }

  const flags: string[] = [];
  if (values.isTyp) flags.push('TYP');
  if (values.isStk) flags.push('STK');
  if (values.isThk) flags.push('THK');
  if (values.isOrigin) flags.push('ORIGIN');
  if (flags.length > 0) {
    parts.push(flags.join(' '));
  }

  return parts.join(' ');
};

const initialValues: DimensionFormValues = {
  dimType: DIMENSION_TYPES.LINEAR,
  value: '0',
  hasMult: false,
  mult: undefined,
  hasTolerance: false,
  toleranceType: TOLERANCE_TYPES.SYMMETRIC,
  toleranceUpper: undefined,
  toleranceLower: undefined,
  toleranceSymmetric: undefined,
  isOrigin: false,
  isTyp: false,
  isStk: false,
  isThk: false,
  role: DIMENSION_ROLES.STANDARD,
  orientation: undefined,
};

const validationSchema = object().shape({
  dimType: createDimensionTypeValidation(),
  value: createRequiredStringNumberField('Value'),
  ...createMultiplierValidationGroup(),
  ...createStringToleranceValidationGroup(),
});

export const DimensionForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: DimensionFormValues) => {
      // Parse the main value to get numeric value for tolerance calculations
      const parsedValue = parsePrecisionValue(values.value);
      if (!parsedValue) {
        // This should be caught by validation, but just in case
        return;
      }

      // Build measurement with tolerance using string-based values
      const toleranceConfig = stringToleranceFormValuesToConfig(
        {
          hasTolerance: values.hasTolerance,
          toleranceType: values.toleranceType,
          toleranceSymmetric: values.toleranceSymmetric,
          toleranceUpper: values.toleranceUpper,
          toleranceLower: values.toleranceLower,
        },
        parsedValue.numericValue, // Use numeric value for tolerance calculations
      );

      const units =
        values.dimType === DIMENSION_TYPES.ANGULAR ? UNITS.DEG : undefined;

      const meas = buildPrecisionMeasurementObject(
        values.value,
        toleranceConfig,
        units,
      );
      if (!meas) {
        // This should be caught by validation, but just in case
        return;
      }

      // Build the final Dimension object
      const dimension: Dimension = {
        calloutType: 'dim',
        dimType: values.dimType,
        mult: extractMultiplier({ hasMult: values.hasMult, mult: values.mult }),
        meas,
        isOrigin: values.isOrigin,
        role: values.role,
        isTyp: values.isTyp,
        isStk: values.isStk,
        isThk: values.isThk,
        orientation: values.orientation ?? null,
      };

      onSubmit(dimension, formatDimensionSummary(values));
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => {
        const previewEnclosure = enclosureShapeFromDimensionRole(values.role);
        return (
          <SubFormWrapper
            isValid={isValid}
            isSubmitting={isSubmitting}
            onCancel={onCancel}
            renderSelectedType={renderSelectedType}
            submitLabel="Add Dimension"
          >
            {/* Preview Section */}
            <FormPreviewSection>
              <Box
                border="1px solid"
                borderColor={
                  previewEnclosure === ENCLOSURE_SHAPES.BOX ||
                  previewEnclosure === ENCLOSURE_SHAPES.OBROUND
                    ? 'currentColor'
                    : 'transparent'
                }
                px={2}
                py={0}
                borderRadius={
                  previewEnclosure === ENCLOSURE_SHAPES.OBROUND
                    ? 'full'
                    : undefined
                }
                display="inline-block"
              >
                <GDTSymbolText size="md">
                  {previewEnclosure === ENCLOSURE_SHAPES.PARENTHESES
                    ? `(${formatDimensionSummary(values)})`
                    : formatDimensionSummary(values)}
                </GDTSymbolText>
              </Box>
            </FormPreviewSection>

            {/* Form Fields */}
            <Flex direction="column" gap={1} px={1} pb={1}>
              {/* Dimension Type Select */}
              <DimensionTypeSelect
                name="dimType"
                label="Dimension Type"
                value={values.dimType}
                onChange={(value) => setFieldValue('dimType', value)}
                error={errors.dimType}
                touched={touched.dimType}
              />

              {/* Dimension Value */}
              <FormControl>
                <FormLabel>Value</FormLabel>
                <NumberValueField
                  name="value"
                  value={values.value}
                  onChange={(value) => setFieldValue('value', value)}
                  size="sm"
                />
                {errors.value && touched.value && (
                  <FormErrorMessage>{errors.value}</FormErrorMessage>
                )}
              </FormControl>

              {/* Tolerance */}
              <ToleranceInputGroup
                hasToleranceFieldName="hasTolerance"
                hasTolerance={values.hasTolerance}
                onHasToleranceChange={(checked) =>
                  setFieldValue('hasTolerance', checked)
                }
                toleranceTypeFieldName="toleranceType"
                toleranceType={values.toleranceType}
                onToleranceTypeChange={(type) =>
                  setFieldValue('toleranceType', type)
                }
                symmetricFieldName="toleranceSymmetric"
                symmetricValue={values.toleranceSymmetric}
                onSymmetricChange={(value) =>
                  setFieldValue('toleranceSymmetric', value)
                }
                upperFieldName="toleranceUpper"
                upperValue={values.toleranceUpper}
                onUpperChange={(value) =>
                  setFieldValue('toleranceUpper', value)
                }
                lowerFieldName="toleranceLower"
                lowerValue={values.toleranceLower}
                onLowerChange={(value) =>
                  setFieldValue('toleranceLower', value)
                }
                errors={errors}
                touched={touched}
              />

              {/* Multiplier */}
              <MultiplierInput
                hasMultFieldName="hasMult"
                hasMult={values.hasMult}
                onHasMultChange={(checked) => setFieldValue('hasMult', checked)}
                multFieldName="mult"
                multValue={values.mult}
                onMultChange={(value) => setFieldValue('mult', value)}
                errors={errors}
                touched={touched}
              />

              <DimensionRoleSelect
                name="role"
                label="Role"
                value={values.role}
                onChange={(value) => setFieldValue('role', value)}
              />

              <FormControl>
                <FormLabel>Dimension Flags</FormLabel>
                <Flex direction="column" gap={1}>
                  <Checkbox
                    name="isTyp"
                    isChecked={values.isTyp}
                    onChange={(e) => setFieldValue('isTyp', e.target.checked)}
                    size="sm"
                  >
                    Typical (TYP)
                  </Checkbox>

                  <Checkbox
                    name="isOrigin"
                    isChecked={values.isOrigin}
                    onChange={(e) =>
                      setFieldValue('isOrigin', e.target.checked)
                    }
                    size="sm"
                  >
                    Origin (ORIGIN)
                  </Checkbox>

                  <Checkbox
                    name="isStk"
                    isChecked={values.isStk}
                    onChange={(e) => setFieldValue('isStk', e.target.checked)}
                    size="sm"
                  >
                    Stock (STK)
                  </Checkbox>

                  <Checkbox
                    name="isThk"
                    isChecked={values.isThk}
                    onChange={(e) => setFieldValue('isThk', e.target.checked)}
                    size="sm"
                  >
                    Thickness (THK)
                  </Checkbox>
                </Flex>
              </FormControl>

              <OrientationSelect
                name="orientation"
                label="Orientation (optional)"
                value={values.orientation}
                onChange={(value) => setFieldValue('orientation', value)}
              />
            </Flex>
          </SubFormWrapper>
        );
      }}
    </Formik>
  );
};
